import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { NavLink as RouterNavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  FiActivity,
  FiBarChart2,
  FiCalendar,
  FiClock,
  FiChevronLeft,
  FiChevronRight,
  FiClipboard,
  FiCreditCard,
  FiDatabase,
  FiDollarSign,
  FiFileText,
  FiGrid,
  FiLogOut,
  FiMenu,
  FiShield,
  FiSliders,
  FiTrendingUp,
  FiUsers,
  FiX,
} from 'react-icons/fi';
import { IconType } from 'react-icons';
import { useAuth } from '../context/AuthContext';
import { SYSMADD_ENVIRONMENT } from '../utils/environment';
import '../styles/Layout.css';

interface LayoutProps {
  children: ReactNode;
}

interface NavigationItem {
  to: string;
  label: string;
  icon: IconType;
  end?: boolean;
}

const routeTitles: Array<[RegExp, string]> = [
  [/^\/$/, 'Visão geral'],
  [/^\/pacientes\/novo$/, 'Novo paciente'],
  [/^\/pacientes\/\d+\/editar$/, 'Editar paciente'],
  [/^\/pacientes\/\d+$/, 'Prontuário do paciente'],
  [/^\/pacientes/, 'Pacientes'],
  [/^\/procedimentos\/novo$/, 'Novo procedimento'],
  [/^\/procedimentos\/\d+$/, 'Detalhes do procedimento'],
  [/^\/agenda-profissionais/, 'Agenda livre por profissional'],
  [/^\/procedimentos/, 'Procedimentos'],
  [/^\/indices/, 'Índices e regras'],
  [/^\/cadastros/, 'Cadastros clínicos'],
  [/^\/relatorios\/procedimentos/, 'Relatório por profissional'],
  [/^\/relatorios\/honorarios/, 'Honorários'],
  [/^\/relatorios\/vendas/, 'Vendas e resultados'],
  [/^\/relatorios\/analises/, 'Análises gerenciais'],
  [/^\/pagamentos/, 'Pagamentos'],
  [/^\/recibos-honorarios/, 'Recibos'],
  [/^\/usuarios/, 'Usuários e auditoria'],
  [/^\/sistema/, 'Manutenção do sistema'],
];

const EnvironmentBadge = () => {
  if (SYSMADD_ENVIRONMENT === 'production') return null;
  const text = SYSMADD_ENVIRONMENT === 'homologation'
    ? 'AMBIENTE DE HOMOLOGAÇÃO'
    : 'MODO DE DESENVOLVIMENTO LOCAL';
  return <div className={`environment-badge environment-${SYSMADD_ENVIRONMENT}`} role="status">{text}</div>;
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(() => localStorage.getItem('sysmadd-sidebar') !== 'closed');
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.role === 'admin' || user?.role === 'system';
  const isSystem = user?.role === 'system';

  useEffect(() => {
    localStorage.setItem('sysmadd-sidebar', sidebarOpen ? 'open' : 'closed');
  }, [sidebarOpen]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const pageTitle = useMemo(
    () => routeTitles.find(([pattern]) => pattern.test(location.pathname))?.[1] || 'SysMadd',
    [location.pathname]
  );

  const primaryItems: NavigationItem[] = [
    ...(isAdmin ? [{ to: '/', label: 'Visão geral', icon: FiGrid, end: true }] : []),
    { to: '/pacientes', label: 'Pacientes', icon: FiUsers },
    { to: '/procedimentos', label: 'Procedimentos', icon: FiCalendar },
    { to: '/agenda-profissionais', label: 'Agenda livre', icon: FiClock },
  ];

  const managementItems: NavigationItem[] = isAdmin ? [
    { to: '/indices', label: 'Índices e regras', icon: FiSliders },
    { to: '/cadastros', label: 'Cadastros', icon: FiClipboard },
    { to: '/pagamentos', label: 'Pagamentos', icon: FiCreditCard },
    { to: '/recibos-honorarios', label: 'Recibos', icon: FiFileText },
  ] : [];

  const reportItems: NavigationItem[] = isAdmin ? [
    { to: '/relatorios/procedimentos', label: 'Participações', icon: FiActivity },
    { to: '/relatorios/honorarios', label: 'Honorários', icon: FiDollarSign },
    { to: '/relatorios/vendas', label: 'Vendas', icon: FiBarChart2 },
    { to: '/relatorios/analises', label: 'Análises', icon: FiTrendingUp },
  ] : [];

  const adminItems: NavigationItem[] = isAdmin ? [
    { to: '/usuarios', label: 'Usuários', icon: FiShield },
    ...(isSystem ? [{ to: '/sistema', label: 'Usuário de Sistema', icon: FiDatabase }] : []),
  ] : [];

  const exit = async () => {
    try {
      await logout();
    } finally {
      navigate('/login', { replace: true });
    }
  };

  const toggleSidebar = () => setSidebarOpen((current) => !current);
  const initials = user?.name?.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || 'SM';
  const roleLabel = user?.role === 'system' ? 'Usuário de Sistema' : user?.role === 'admin' ? 'Administrador' : 'Atendimento';

  return (
    <div className={`layout ${sidebarOpen ? 'sidebar-expanded' : 'sidebar-collapsed'}`}>
      {mobileOpen && <button className="sidebar-backdrop" type="button" aria-label="Fechar menu" onClick={() => setMobileOpen(false)} />}

      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'} ${mobileOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="logo" aria-label="SysMadd">
            <span className="logo-icon"><FiActivity /></span>
            <span className="logo-copy">
              <strong>SysMadd</strong>
              <small>Gestão Clínica</small>
            </span>
          </div>
          <button className="mobile-sidebar-close" type="button" aria-label="Fechar menu" onClick={() => setMobileOpen(false)}><FiX /></button>
        </div>

        <nav className="sidebar-nav" aria-label="Navegação principal">
          <NavigationGroup label="Clínica" items={primaryItems} open={sidebarOpen} />
          {managementItems.length > 0 && <NavigationGroup label="Gestão" items={managementItems} open={sidebarOpen} />}
          {reportItems.length > 0 && <NavigationGroup label="Relatórios" items={reportItems} open={sidebarOpen} />}
          {adminItems.length > 0 && <NavigationGroup label="Administração" items={adminItems} open={sidebarOpen} />}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-clinic-status">
            <span className="status-dot" />
            {sidebarOpen && <span>Sistema operacional</span>}
          </div>
          <button
            className="toggle-sidebar"
            type="button"
            aria-label={sidebarOpen ? 'Recolher menu' : 'Expandir menu'}
            onClick={toggleSidebar}
          >
            {sidebarOpen ? <FiChevronLeft /> : <FiChevronRight />}
            {sidebarOpen && <span>Recolher menu</span>}
          </button>
        </div>
      </aside>

      <main className="main-content">
        <EnvironmentBadge />
        <header className="top-bar">
          <div className="top-bar-title">
            <button className="mobile-menu-button" type="button" aria-label="Abrir menu" onClick={() => setMobileOpen(true)}><FiMenu /></button>
            <div>
              <span>SysMadd / Clínica</span>
              <h1>{pageTitle}</h1>
            </div>
          </div>

          <div className="user-menu">
            <div className="user-avatar" aria-hidden="true">{initials}</div>
            <div className="user-identity">
              <strong>{user?.name}</strong>
              <span>{roleLabel}</span>
            </div>
            <button className="logout-button" type="button" onClick={exit} title="Sair do sistema">
              <FiLogOut />
              <span>Sair</span>
            </button>
          </div>
        </header>
        <div className="content">{children}</div>
      </main>
    </div>
  );
};

const NavigationGroup: React.FC<{ label: string; items: NavigationItem[]; open: boolean }> = ({ label, items, open }) => (
  <div className="nav-group">
    {open && <span className="nav-group-label">{label}</span>}
    {items.map(({ to, label: itemLabel, icon: Icon, end }) => (
      <RouterNavLink
        key={to}
        to={to}
        end={end}
        title={!open ? itemLabel : undefined}
        className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
      >
        <span className="icon"><Icon /></span>
        {open && <span>{itemLabel}</span>}
      </RouterNavLink>
    ))}
  </div>
);

export default Layout;
